/* eslint-disable react/jsx-curly-newline */
/* eslint-disable react/jsx-one-expression-per-line */
import React, { useContext, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import {
  TableBody,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@material-ui/core';
import { useRecoilState, useSetRecoilState } from 'recoil';
import LoadingOverlay from 'react-loading-overlay';
import AuthenticatedContext from '../../context/AuthenticatedContext';
import {
  backPageFlg,
  eqTroubleListPageInfo,
  pageTitle,
} from '../../context/PageRecoil';
import { pathnames } from '../../api';
import useStyles, { headerWidth } from '../../css/style';
import Paging from '../paging';
import { OneEquipment } from '../../models/data/Equipment';
import { requestOneEquipment } from '../../api/equipment';
import DataDisp from './datadisp';
import { routeName } from '../../App';
import requestEqTroubleList, {
  requestCountEqTrouble,
} from '../../api/eqTrouble';
import EqTrouble from '../../models/data/EqTrouble';
import useErrorHandling from '../errorCheck';
import { dateformat } from '../date-range';

type UserPageParams = {
  id: string;
};

/**
 * 設備点検履歴画面
 */
const EqTroubleList: React.FunctionComponent = () => {
  const history = useHistory();
  const [pageInfo, setPageInfo] = useRecoilState(eqTroubleListPageInfo);
  const authContext = useContext(AuthenticatedContext); // ログインユーザの情報を取得
  const classes = useStyles();
  const [loading, setLoading] = useState(true); // ロード画面の状態管理
  const { id } = useParams<UserPageParams>();
  const [backFlg, setBackFlg] = useRecoilState(backPageFlg);
  const [data, setData] = useState<EqTrouble[]>([]); // 表示するメインデータ
  const [subdata, setSubData] = useState<OneEquipment>();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [scroll, setScroll] = useState<number>(0); // テーブルのスクロール位置
  const { errorCheck } = useErrorHandling(); // エラー処理

  const setPageTitle = useSetRecoilState(pageTitle);
  React.useEffect(() => {
    return setPageTitle({ pageTitle: '設備故障修理履歴' }); // ページのタイトルを設定
  }, [setPageTitle]);

  // APIのURL
  const urlEqTroubleList = history.createHref({
    pathname: pathnames.eqTroubleList,
  }); // 設備故障履歴一覧取得
  const urlCounteqTrouble = history.createHref({
    pathname: pathnames.eqTroubleCount,
  }); // 設備故障履歴件数取得
  const urlOneEquipment = history.createHref({
    pathname: pathnames.oneEquipment,
  }); // 設備情報取得
  // ページング用の変数
  const [totalData, setTotalData] = useState<number>(0); // データ数
  const [currentPage, setCurrentPage] = useState<number>(1); // 現在のページ
  const perPage = 20; // 一覧一ページあたりの表示件数

  // ソート用の変数
  type SortConfigType = { key: string; direction: string };
  const [sortConfig, setSortConfig] = React.useState<SortConfigType>({
    key: '',
    direction: '',
  });

  const headerCellProps = [
    {
      width: headerWidth.troubleEqId,
      sortKey: 'troubleEqId',
      text: '設備故障ID',
      className: classes.header,
    },
    {
      width: headerWidth.trEqTrName,
      sortKey: 'trEqTrName',
      text: '作業名',
      className: classes.header,
    },
    {
      width: headerWidth.trEqSummary,
      sortKey: 'trEqSummary',
      text: '内容',
      className: classes.header,
    },
    {
      width: headerWidth.trEqName,
      sortKey: 'trEqName',
      text: '担当',
      className: classes.header,
    },
    {
      width: headerWidth.trEqStartDate,
      sortKey: 'trEqStartDate',
      text: '開始年月日',
      sortKey2: 'trEqEndDate',
      text2: '終了年月日',
      className: classes.header,
    },
    {
      width: headerWidth.trEqJobNo,
      sortKey: 'trEqJobNo',
      text: '工号',
      className: classes.header,
    },
    {
      width: headerWidth.trEqReportNo,
      sortKey: 'trEqReportNo',
      text: '報告書番号',
      className: classes.header,
    },
  ];

  // 画面遷移で送られた情報を設定する
  const locationSet = () => {
    // 初期値のままの場合
    if (currentPage === 0) {
      setCurrentPage(1);
    }
    // 戻るでの状態再現のフラグがTrueの場合
    if (backFlg.eqTroubleList) {
      setSortConfig({
        key: pageInfo.sortKey,
        direction: pageInfo.sortDirection,
      });
      setCurrentPage(pageInfo.page);
      // 一覧のテーブルがあるか確認
      if (containerRef.current) {
        setScroll(pageInfo.scrollPosition);
      }
      setBackFlg({ ...backFlg, eqTroubleList: false });
    }
  };

  // 一覧のページ変更処理
  const pageChange = (num: number) => {
    setScroll(0);
    setCurrentPage((prevPage) => {
      let newPage = prevPage + num;
      const tempTotalPages = Math.ceil(totalData / perPage);
      const totalPages = tempTotalPages > 0 ? tempTotalPages : 1;

      if (newPage < 1) newPage = 1;
      if (newPage > totalPages) newPage = totalPages;

      return newPage;
    });
  };

  // ソート機能の関数など
  const sortStart = (key: string) => {
    pageChange(1 - currentPage); // はじめのページに戻る
    setScroll(0); // 表のスクロールバーをリセット
    let direction = 'asc';
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'asc'
    ) {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // ページ遷移時の処理
  const movePage = (route: string) => {
    const tempScroll = containerRef.current
      ? containerRef.current.scrollTop
      : 0;
    setPageInfo({
      page: currentPage,
      scrollPosition: tempScroll,
      sortKey: sortConfig.key,
      sortDirection: sortConfig.direction,
    });
    history.push(route);
  };

  // 初回画面表示時の処理
  useEffect(() => {
    setLoading(true); // 非同期処理前にsetLoading(true)
    locationSet();
    Promise.all([
      requestOneEquipment(urlOneEquipment, {
        equipmentId: Number(id),
      }),
    ])
      .then((responses) => {
        setSubData(responses[0].data); // 設備情報
      })
      .catch((error) => {
        errorCheck(error); // エラー内容を確認
      })
      .finally(() => {
        setLoading(false); // 非同期処理後にsetLoading(false)
      });
  }, []);

  // データ更新時の処理
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = scroll;
    }
  }, [data]);

  // 設備情報の取得
  useEffect(() => {
    setLoading(true); // 非同期処理前にsetLoading(true)
    Promise.all([
      requestCountEqTrouble(urlCounteqTrouble, {
        custEqId: Number(id),
      }),
      requestEqTroubleList(urlEqTroubleList, {
        custEqId: Number(id),
        number: perPage,
        page: currentPage,
        sortKey: sortConfig.key,
        sortDirection: sortConfig.direction,
      }),
    ])
      .then((responses) => {
        setTotalData(responses[0].data); // 件数
        setData(responses[1].data); // 設備情報
      })
      .catch((error) => {
        errorCheck(error); // エラー内容を確認
      })
      .finally(() => {
        setLoading(false); // 非同期処理後にsetLoading(false)
      });
  }, [currentPage, sortConfig]);

  return (
    <LoadingOverlay
      active={loading}
      className={classes.load}
      spinner
      text="データを読み込んでいます..."
    >
      <div className={classes.root}>
        <Button
          variant="primary"
          className="my-2"
          onClick={() => {
            history.push(`${routeName.eqMaintMenu}/${id}`);
          }}
        >
          戻る
        </Button>
        <br />
        <DataDisp userdata={authContext} data={subdata} />
        <div className={`${classes.paging} ${classes.eqTroubleListWidth}`}>
          <Paging
            totalData={totalData}
            currentPage={currentPage}
            perPage={perPage}
            onPageChange={pageChange}
          />
        </div>
        <TableContainer
          component={Paper}
          className={`${classes.container} ${classes.eqTroubleListHeight} ${classes.eqTroubleListWidth}`}
          ref={containerRef}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {/* ヘッダのセルを生成 */}
                {headerCellProps.map(
                  ({ width, sortKey, text, sortKey2, text2, className }) => (
                    <TableCell width={width} className={className}>
                      <Button
                        variant="link"
                        onClick={() => sortStart(sortKey)}
                        className={classes.linkButton}
                      >
                        {text}
                        {sortConfig &&
                          sortConfig.key === sortKey &&
                          (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </Button>
                      {/* ソートが2つある場合（数量単位用） */}
                      {sortKey2 != null && text2 != null && (
                        <Button
                          variant="link"
                          onClick={() => sortStart(sortKey2)}
                          className={classes.linkButton}
                        >
                          {text2}
                          {sortConfig &&
                            sortConfig.key === sortKey2 &&
                            (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </Button>
                      )}
                    </TableCell>
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((d, index) => (
                <TableRow
                  className={index % 2 === 0 ? classes.evenRow : classes.oddRow}
                >
                  <TableCell className={`${classes.cell} ${classes.numRow}`}>
                    <Button
                      variant="link"
                      onClick={() =>
                        movePage(
                          `${routeName.eqTroubleDetail}/${id}/${
                            d.troubleEqId ?? ''
                          }`,
                        )
                      }
                    >
                      {d.troubleEqId ?? ''}
                    </Button>
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.stringRow}`}>
                    {d.trEqTrName}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.stringRow}`}>
                    {d.trEqSummary}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.stringRow}`}>
                    {d.trEqName}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.stringRow}`}>
                    {dateformat(d.trEqEndDate)}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.stringRow}`}>
                    {d.trEqJobNo}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.stringRow}`}>
                    {d.trEqReportNo}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </LoadingOverlay>
  );
};

export default EqTroubleList;
